import SharesChart from "components/charts/modern/SharesChart";
import SharesholdersChart from "components/charts/modern/SharesholdersChart";
import TradesChart from "components/charts/modern/TradesChart";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { CompanyLiquidityTabEnum } from "components/company/v3/menu/enums";
import { Tabs } from "components/ui/Tabs";
import TickerSymbol from "lib/securities/TickerSymbol";
import { AreaChartIcon, BarChart3Icon, ContactIcon } from "lucide-react";
import { useIntl } from "react-intl";
import type { Updater } from "react-powerplug";
import { Value } from "react-powerplug";

interface BrazilianLiquityBoxProps {
  tickerSymbol: string;
  tab: CompanyLiquidityTabEnum;
  onChangeTab: (tab: CompanyLiquidityTabEnum) => void;
}

export default function BrazilianLiquityBox({ tickerSymbol, tab, onChangeTab }: BrazilianLiquityBoxProps) {
  const intl = useIntl();
  const issuer = useBrazilianIssuer();

  const tickerSymbolsWithQuotes = issuer.securities.map((node) => node.tickerSymbol);
  const availableTickerSymbols = tickerSymbolsWithQuotes.filter((tickerSymbol) => {
    return !TickerSymbol.isUnitStockSymbol(tickerSymbol);
  });

  const hasAvailableTickerSymbols = availableTickerSymbols.length > 0;

  return (
    <CompanyContainer>
      <CompanyContentContainer>
        <Tabs
          defaultValue={CompanyLiquidityTabEnum.TRADE_HISTORY}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyLiquidityTabEnum)}
          className="h-full w-full"
        >
          <CompanyTabsList
            activeTab={tab}
            tabs={[
              {
                value: CompanyLiquidityTabEnum.TRADE_HISTORY,
                label: intl.formatMessage({
                  defaultMessage: "Negócios",
                  description: "Aba do menu superior da empresa",
                  id: "aU3x7h",
                }),
                icon: <BarChart3Icon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyLiquidityTabEnum.SHARES_OUTSTANDING,
                label: intl.formatMessage({
                  defaultMessage: "Ações em Circulação",
                  description: "Aba do menu superior da empresa",
                  id: "IPt9zK",
                }),
                icon: <AreaChartIcon className="inline-flex" size={16} strokeWidth={2} />,
                disabled: !hasAvailableTickerSymbols,
              },
              {
                value: CompanyLiquidityTabEnum.SHARES_HOLDERS,
                label: intl.formatMessage({
                  defaultMessage: "Investidores",
                  description: "Aba do menu superior da empresa",
                  id: "F3HmQX",
                }),
                icon: <ContactIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
            ]}
          />

          <CompanyTabsContent value={CompanyLiquidityTabEnum.TRADE_HISTORY}>
            <Value<string[]>
              key={`TradingSessionsChart/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <TradesChart
                  tickerSymbols={value}
                  availableTickerSymbols={tickerSymbolsWithQuotes}
                  onToggleTickerSymbols={(tickerSymbols) => {
                    onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                  }}
                />
              )}
            </Value>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyLiquidityTabEnum.SHARES_OUTSTANDING}>
            <Value<string[]>
              key={`SharesChart/${availableTickerSymbols.join("")}`}
              initial={[availableTickerSymbols[0]]}
            >
              {({ value, set }) => (
                <SharesChart
                  tickerSymbols={value}
                  availableTickerSymbols={availableTickerSymbols}
                  onToggleTickerSymbols={(tickerSymbols) => {
                    onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set);
                  }}
                />
              )}
            </Value>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyLiquidityTabEnum.SHARES_HOLDERS}>
            <SharesholdersChart tickerSymbols={[tickerSymbol]} />
          </CompanyTabsContent>
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}

function onToggleTickerSymbols(tickerSymbol: string, tickerSymbols: string[], set: Updater<string[]>) {
  if (tickerSymbols.length === 0) {
    set([tickerSymbol]);
  } else {
    set(tickerSymbols);
  }
}
